import type {
  AdminStory,
  AdminStoryMedia,
  ApproveStoryBody,
  CreateStoryInvitationBody,
  CreatedStoryInvitation,
  InitiateStoryMediaBody,
  CompleteStoryMediaBody,
  InitiateStoryMediaResponse,
  ListAdminStoryMediaResponse,
  ListPublicStoryMediaResponse,
  SignStoryMediaPartsBody,
  SignStoryMediaPartsResponse,
  ListStoriesResponse,
  PublicStory,
  PublicStoryMedia,
  RejectStoryBody,
  RejectStoryMediaBody,
  StoryDraft,
  StoryMediaUrlResponse,
  SubmitStoryBody,
  SubmitStoryResponse,
  UpdateAdminStoryBody,
  UpdateStoryDraftBody,
} from '@fois-utsavvoice/shared';
import { apiRequest } from './client.js';
import { uploadPhotoToPresignedUrl } from './public.js';

export function createStoryInvitation(
  accessToken: string,
  body: CreateStoryInvitationBody,
): Promise<CreatedStoryInvitation> {
  return apiRequest<CreatedStoryInvitation>('/v1/admin/stories', {
    method: 'POST',
    accessToken,
    body: JSON.stringify(body),
  });
}

export function listStories(
  accessToken: string,
  status?: string,
): Promise<ListStoriesResponse> {
  const suffix = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiRequest<ListStoriesResponse>(`/v1/admin/stories${suffix}`, { accessToken });
}

export function getAdminStory(accessToken: string, storyId: string): Promise<AdminStory> {
  return apiRequest<AdminStory>(`/v1/admin/stories/${storyId}`, { accessToken });
}

export function updateAdminStory(
  accessToken: string,
  storyId: string,
  body: UpdateAdminStoryBody,
): Promise<AdminStory> {
  return apiRequest<AdminStory>(`/v1/admin/stories/${storyId}`, {
    method: 'PATCH',
    accessToken,
    body: JSON.stringify(body),
  });
}

export function beginStoryReview(accessToken: string, storyId: string): Promise<AdminStory> {
  return apiRequest<AdminStory>(`/v1/admin/stories/${storyId}/begin-review`, {
    method: 'POST',
    accessToken,
  });
}

export function approveStory(
  accessToken: string,
  storyId: string,
  body: ApproveStoryBody,
): Promise<AdminStory> {
  return apiRequest<AdminStory>(`/v1/admin/stories/${storyId}/approve`, {
    method: 'POST',
    accessToken,
    body: JSON.stringify(body),
  });
}

export function rejectStory(
  accessToken: string,
  storyId: string,
  body: RejectStoryBody,
): Promise<AdminStory> {
  return apiRequest<AdminStory>(`/v1/admin/stories/${storyId}/reject`, {
    method: 'POST',
    accessToken,
    body: JSON.stringify(body),
  });
}

export function archiveStory(accessToken: string, storyId: string): Promise<AdminStory> {
  return apiRequest<AdminStory>(`/v1/admin/stories/${storyId}/archive`, {
    method: 'POST',
    accessToken,
  });
}

export function getPublicStory(token: string): Promise<PublicStory> {
  return apiRequest<PublicStory>(`/v1/public/stories/${encodeURIComponent(token)}`);
}

export function getPublicStoryDraft(token: string): Promise<StoryDraft> {
  return apiRequest<StoryDraft>(`/v1/public/stories/${encodeURIComponent(token)}/draft`);
}

export function savePublicStoryDraft(
  token: string,
  body: UpdateStoryDraftBody,
): Promise<StoryDraft> {
  return apiRequest<StoryDraft>(`/v1/public/stories/${encodeURIComponent(token)}/draft`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function submitPublicStory(
  token: string,
  body: SubmitStoryBody,
  idempotencyKey: string,
): Promise<SubmitStoryResponse> {
  return apiRequest<SubmitStoryResponse>(`/v1/public/stories/${encodeURIComponent(token)}/submit`, {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(body),
  });
}

export function initiateStoryPhoto(
  token: string,
  body: InitiateStoryMediaBody,
  idempotencyKey: string,
): Promise<InitiateStoryMediaResponse> {
  return apiRequest<InitiateStoryMediaResponse>(
    `/v1/public/stories/${encodeURIComponent(token)}/media/initiate`,
    {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(body),
    },
  );
}

export function signStoryMediaParts(
  token: string,
  mediaId: string,
  body: SignStoryMediaPartsBody,
): Promise<SignStoryMediaPartsResponse> {
  return apiRequest<SignStoryMediaPartsResponse>(
    `/v1/public/stories/${encodeURIComponent(token)}/media/${mediaId}/parts`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

export function completeStoryPhoto(
  token: string,
  mediaId: string,
  body: CompleteStoryMediaBody = {},
): Promise<PublicStoryMedia> {
  return apiRequest<PublicStoryMedia>(
    `/v1/public/stories/${encodeURIComponent(token)}/media/${mediaId}/complete`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

export function removeStoryPhoto(token: string, mediaId: string): Promise<PublicStoryMedia> {
  return apiRequest<PublicStoryMedia>(
    `/v1/public/stories/${encodeURIComponent(token)}/media/${mediaId}`,
    { method: 'DELETE' },
  );
}

export function listPublicStoryMedia(token: string): Promise<ListPublicStoryMediaResponse> {
  return apiRequest<ListPublicStoryMediaResponse>(
    `/v1/public/stories/${encodeURIComponent(token)}/media`,
  );
}

export function uploadStoryPart(
  uploadUrl: string,
  blob: Blob,
  headers: Record<string, string>,
  onProgress?: (percent: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.getResponseHeader('ETag') ?? xhr.getResponseHeader('etag') ?? `"${xhr.status}"`);
        return;
      }
      reject(new Error('VIDEO_PART_FAILED'));
    };
    xhr.onerror = () => reject(new Error('VIDEO_PART_FAILED'));
    xhr.send(blob);
  });
}

export function uploadStoryPhoto(
  uploadUrl: string,
  file: File,
  headers: Record<string, string>,
  onProgress?: (percent: number) => void,
): Promise<void> {
  if (!onProgress) {
    return uploadPhotoToPresignedUrl(uploadUrl, file, headers);
  }
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error('PHOTO_UPLOAD_FAILED'));
    };
    xhr.onerror = () => reject(new Error('PHOTO_UPLOAD_FAILED'));
    xhr.send(file);
  });
}

export function listAdminStoryMedia(
  accessToken: string,
  storyId: string,
): Promise<ListAdminStoryMediaResponse> {
  return apiRequest<ListAdminStoryMediaResponse>(`/v1/admin/stories/${storyId}/media`, {
    accessToken,
  });
}

export function getAdminStoryMediaViewUrl(
  accessToken: string,
  storyId: string,
  mediaId: string,
): Promise<StoryMediaUrlResponse> {
  return apiRequest<StoryMediaUrlResponse>(
    `/v1/admin/stories/${storyId}/media/${mediaId}/view-url`,
    { accessToken },
  );
}

export function getAdminStoryMediaDownloadUrl(
  accessToken: string,
  storyId: string,
  mediaId: string,
): Promise<StoryMediaUrlResponse> {
  return apiRequest<StoryMediaUrlResponse>(
    `/v1/admin/stories/${storyId}/media/${mediaId}/download-url`,
    { accessToken },
  );
}

export function approveStoryMedia(
  accessToken: string,
  storyId: string,
  mediaId: string,
): Promise<AdminStoryMedia> {
  return apiRequest<AdminStoryMedia>(`/v1/admin/stories/${storyId}/media/${mediaId}/approve`, {
    method: 'POST',
    accessToken,
  });
}

export function rejectStoryMedia(
  accessToken: string,
  storyId: string,
  mediaId: string,
  body: RejectStoryMediaBody,
): Promise<AdminStoryMedia> {
  return apiRequest<AdminStoryMedia>(`/v1/admin/stories/${storyId}/media/${mediaId}/reject`, {
    method: 'POST',
    accessToken,
    body: JSON.stringify(body),
  });
}
